import { useState } from 'react';
import { CheckCircle2, XCircle, Users, Eye, MousePointer, CreditCard, ChevronDown, Info } from 'lucide-react';
import { getScoreColor } from '@/lib/utils';

interface TrackingGap {
  category: string;
  event: string;
  description: string;
  whyItMatters?: string;
  priority: 'critical' | 'high' | 'medium' | 'low';
}

interface TrackedItem {
  category: string;
  event: string;
  description: string;
}

interface ConversionFunnelProps {
  tracking: {
    gtm: { present: boolean };
    ga4: { present: boolean };
    facebookPixel: { present: boolean };
    events?: {
      trackingGaps?: TrackingGap[];
      trackedItems?: TrackedItem[];
      toastIntegration: {
        hasToastLinks: boolean;
        toastLinksTracked: boolean;
        toastUrls: string[];
      };
      trackingCoverage?: {
        overallScore: number;
      };
    };
    orderingPlatforms?: Array<{
      platform: string;
      platformName: string;
      linkCount: number;
      isTracked: boolean;
    }>;
  };
}

interface FunnelStage {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  isTracked: boolean;
  trackingDetails?: string;
  trackingIssue?: string;
  importance: 'critical' | 'high' | 'medium';
  matchedTracked: TrackedItem[];
  matchedGaps: TrackingGap[];
}

// Keywords to map tracked items and gaps to funnel stages
const STAGE_KEYWORDS: Record<string, string[]> = {
  visitor: ['page_view', 'pageview', 'session', 'page tracking'],
  menu_view: ['menu_view', 'menu', 'engagement'],
  action_click: ['reservation', 'reserve', 'order', 'click', 'conversions', 'cta', 'online_order', 'toast'],
  conversion: ['conversion', 'purchase', 'complete', 'submit', 'transaction'],
};

function matchesToStage(stageId: string, item: { event: string; category: string }): boolean {
  const keywords = STAGE_KEYWORDS[stageId] || [];
  const eventLower = item.event.toLowerCase();
  const categoryLower = item.category.toLowerCase();
  return keywords.some(kw => eventLower.includes(kw) || categoryLower.includes(kw));
}

const FUNNEL_WIDTHS = ['max-w-full', 'max-w-[85%]', 'max-w-[70%]', 'max-w-[55%]'];

export function ConversionFunnel({ tracking }: ConversionFunnelProps) {
  const gaps = tracking.events?.trackingGaps || [];
  const tracked = tracking.events?.trackedItems || [];
  const platforms = tracking.orderingPlatforms || [];

  // Determine tracking status for each funnel stage
  const hasPageViewTracking = tracking.ga4.present || tracking.gtm.present;
  const hasMenuViewTracking = !gaps.some(g => g.event === 'menu_view');
  const hasReservationTracking = tracked.some(t =>
    t.event.includes('reservation') || t.event.includes('reserve') || t.category.includes('reservation')
  ) || !gaps.some(g => g.event === 'reservation_click');

  // Check ordering tracking across all platforms
  const orderingGapEvents = ['order_click', 'ordering_click', 'online_order_click'];
  const orderingPlatformsTracked = platforms.length === 0
    ? !gaps.some(g => orderingGapEvents.includes(g.event))
    : platforms.every(p => p.isTracked);

  const hasOrderingTracking = orderingPlatformsTracked ||
    tracking.events?.toastIntegration?.toastLinksTracked ||
    !gaps.some(g => orderingGapEvents.includes(g.event));

  function getActionTrackingDetails() {
    const details: string[] = [];
    if (hasReservationTracking) details.push('Reservations');
    if (hasOrderingTracking) {
      const trackedPlatforms = platforms.filter(p => p.isTracked).map(p => p.platformName);
      if (trackedPlatforms.length > 0) {
        details.push(trackedPlatforms.join(', '));
      } else if (tracking.events?.toastIntegration?.toastLinksTracked) {
        details.push('Toast');
      } else {
        details.push('Orders');
      }
    }
    return details.length > 0 ? details.join(' & ') + ' tracked' : undefined;
  }

  function getActionTrackingIssue() {
    const issues: string[] = [];
    if (!hasReservationTracking) issues.push('Reservation clicks not tracked');
    if (!hasOrderingTracking) {
      const untrackedPlatforms = platforms.filter(p => !p.isTracked).map(p => p.platformName);
      if (untrackedPlatforms.length > 0) {
        issues.push(`${untrackedPlatforms.join(', ')} not tracked`);
      }
    }
    return issues.length > 0 ? issues.join('; ') : undefined;
  }

  // Build stages with matched tracked/gap items
  const stages: FunnelStage[] = [
    {
      id: 'visitor',
      name: 'Site Visitor',
      icon: <Users className="h-5 w-5" />,
      description: 'User lands on website',
      isTracked: hasPageViewTracking,
      trackingDetails: hasPageViewTracking
        ? `${tracking.ga4.present ? 'GA4' : ''}${tracking.ga4.present && tracking.gtm.present ? ' + ' : ''}${tracking.gtm.present ? 'GTM' : ''} Active`
        : undefined,
      trackingIssue: !hasPageViewTracking ? 'No analytics tracking installed' : undefined,
      importance: 'critical',
      matchedTracked: tracked.filter(t => matchesToStage('visitor', t)),
      matchedGaps: gaps.filter(g => matchesToStage('visitor', g)),
    },
    {
      id: 'menu_view',
      name: 'Menu View',
      icon: <Eye className="h-5 w-5" />,
      description: 'Views menu or offerings',
      isTracked: hasMenuViewTracking,
      trackingDetails: hasMenuViewTracking ? 'Menu views tracked' : undefined,
      trackingIssue: !hasMenuViewTracking ? 'Menu page views not tracked' : undefined,
      importance: 'high',
      matchedTracked: tracked.filter(t => matchesToStage('menu_view', t)),
      matchedGaps: gaps.filter(g => matchesToStage('menu_view', g)),
    },
    {
      id: 'action_click',
      name: 'Reserve / Order Click',
      icon: <MousePointer className="h-5 w-5" />,
      description: 'Clicks reservation or order button',
      isTracked: hasReservationTracking || hasOrderingTracking,
      trackingDetails: getActionTrackingDetails(),
      trackingIssue: getActionTrackingIssue(),
      importance: 'critical',
      matchedTracked: tracked.filter(t => matchesToStage('action_click', t)),
      matchedGaps: gaps.filter(g => matchesToStage('action_click', g)),
    },
    {
      id: 'conversion',
      name: 'Conversion',
      icon: <CreditCard className="h-5 w-5" />,
      description: 'Completes reservation or order',
      isTracked: hasReservationTracking && hasOrderingTracking,
      trackingDetails: (hasReservationTracking && hasOrderingTracking)
        ? 'Conversion paths tracked'
        : undefined,
      trackingIssue: (!hasReservationTracking || !hasOrderingTracking)
        ? 'Some conversion paths untracked'
        : undefined,
      importance: 'critical',
      matchedTracked: tracked.filter(t => matchesToStage('conversion', t)),
      matchedGaps: gaps.filter(g => matchesToStage('conversion', g)),
    },
  ];

  const trackedCount = stages.filter(s => s.isTracked).length;
  const coveragePercent = Math.round((trackedCount / stages.length) * 100);

  // Gaps not matched to any stage (events, social, etc.)
  const matchedGapEvents = new Set(stages.flatMap(s => s.matchedGaps.map(g => g.event)));
  const unmatchedGaps = gaps.filter(g =>
    !matchedGapEvents.has(g.event) && (g.priority === 'critical' || g.priority === 'high')
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Conversion Funnel</h3>
          <p className="text-sm text-muted-foreground">
            Click each stage to see what's tracked and what's missing
          </p>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-light ${getScoreColor(coveragePercent)}`}>
            {coveragePercent}%
          </p>
          <p className="text-xs text-muted-foreground">stages tracked</p>
        </div>
      </div>

      {/* Funnel Visualization */}
      <div className="space-y-3">
        {stages.map((stage, index) => (
          <div key={stage.id} className={`${FUNNEL_WIDTHS[index]} mx-auto`}>
            <FunnelStageRow stage={stage} />
            {index < stages.length - 1 && (
              <div className="flex justify-center mt-3">
                <div className="w-px h-4 bg-border" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Platform Breakdown */}
      {platforms.length > 0 && (
        <section className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Ordering Platform Tracking
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {platforms.map((platform) => (
              <div
                key={platform.platform}
                className={`flex items-center justify-between p-3 rounded-lg bg-secondary/30 border-l-2 ${
                  platform.isTracked ? 'border-l-green-500/60' : 'border-l-red-500/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  {platform.isTracked ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500/70" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                  <span className="text-sm font-medium">{platform.platformName}</span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {platform.linkCount} link{platform.linkCount !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Unmatched high-priority gaps */}
      {unmatchedGaps.length > 0 && (
        <section className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
            Other Revenue Data You're Missing
          </h4>
          <div className="space-y-2">
            {unmatchedGaps.map((gap, i) => (
              <GapAccordion key={i} gap={gap} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function FunnelStageRow({ stage }: { stage: FunnelStage }) {
  const [expanded, setExpanded] = useState(false);

  const borderColor = stage.isTracked
    ? 'border-l-green-500/60'
    : 'border-l-red-500/40';

  const hasDetails = stage.matchedTracked.length > 0 || stage.matchedGaps.length > 0;

  return (
    <div className={`bg-secondary/30 rounded-lg overflow-hidden border-l-2 ${borderColor}`}>
      <button
        onClick={() => hasDetails && setExpanded(!expanded)}
        className={`w-full text-left p-4 flex items-center gap-4 transition-colors ${
          hasDetails ? 'hover:bg-secondary/50 cursor-pointer' : 'cursor-default'
        }`}
      >
        <div className="text-muted-foreground shrink-0">{stage.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{stage.name}</span>
            {stage.isTracked ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-green-500/70" />
            ) : (
              <XCircle className="h-3.5 w-3.5 text-red-400/70" />
            )}
          </div>
          <p className="text-xs text-muted-foreground">{stage.description}</p>
        </div>
        <div className="text-right text-xs shrink-0 flex items-center gap-2">
          <div>
            {stage.trackingDetails && (
              <p className="text-muted-foreground">{stage.trackingDetails}</p>
            )}
            {stage.trackingIssue && (
              <p className="text-red-400/80">{stage.trackingIssue}</p>
            )}
          </div>
          {hasDetails && (
            <ChevronDown className={`h-4 w-4 text-muted-foreground/40 shrink-0 transition-transform duration-200 ${
              expanded ? 'rotate-180' : ''
            }`} />
          )}
        </div>
      </button>

      {/* Expandable detail panel */}
      <div className={`overflow-hidden transition-all duration-200 ${
        expanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 pb-4 pt-1 border-t border-border/30 space-y-3">
          {/* Currently tracked events */}
          {stage.matchedTracked.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tracked</p>
              {stage.matchedTracked.map((item, i) => (
                <div key={i} className="flex items-start gap-2 pl-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500/70 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <span className="text-sm">{item.event}</span>
                    <span className="text-xs text-muted-foreground ml-2">{item.description}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Missing events (gaps) */}
          {stage.matchedGaps.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Not Tracked</p>
              {stage.matchedGaps.map((gap, i) => (
                <div key={i} className="flex items-start gap-2 pl-1">
                  <XCircle className="h-3.5 w-3.5 text-red-400/70 shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{gap.event}</span>
                      <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${
                        gap.priority === 'critical' ? 'bg-red-500/10 text-red-400'
                          : gap.priority === 'high' ? 'bg-orange-500/10 text-orange-400'
                          : 'bg-secondary text-muted-foreground'
                      }`}>{gap.priority}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{gap.description}</p>
                    {gap.whyItMatters && (
                      <div className="flex items-start gap-1.5 mt-1">
                        <Info className="h-3 w-3 text-muted-foreground/40 shrink-0 mt-0.5" />
                        <p className="text-xs text-foreground/60 italic">{gap.whyItMatters}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty state — stage is tracked but no specific items matched */}
          {stage.matchedTracked.length === 0 && stage.matchedGaps.length === 0 && stage.isTracked && (
            <p className="text-xs text-muted-foreground italic pl-1">
              This stage is covered by your analytics setup.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function GapAccordion({ gap }: { gap: TrackingGap }) {
  const [expanded, setExpanded] = useState(false);

  const accentColor = gap.priority === 'critical'
    ? 'border-l-red-500/60'
    : 'border-l-orange-500/60';

  const priorityLabel = gap.priority === 'critical' ? 'Critical' : 'High';

  return (
    <div className={`rounded-lg border border-border/50 overflow-hidden border-l-2 ${accentColor}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-secondary/20 transition-colors"
      >
        <ChevronDown className={`h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5 transition-transform duration-200 ${
          expanded ? 'rotate-180' : ''
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{gap.event}</span>
            <span className="text-xs text-muted-foreground/60 uppercase">{priorityLabel}</span>
            <span className="text-xs text-muted-foreground px-2 py-0.5 bg-secondary rounded">{gap.category}</span>
          </div>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${
        expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 pb-4 pt-1 ml-7 space-y-2">
          <p className="text-sm text-muted-foreground leading-relaxed">{gap.description}</p>
          {gap.whyItMatters && (
            <div className="flex items-start gap-2 pt-1">
              <Info className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/70 italic">{gap.whyItMatters}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
